import { useEffect, useState } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ensureBotConfigForUrlPath, normalizeDirectUrlPath, stripLegacyRegulatorySuffix} from '../config/getBotConfig';
import { setCurrentConfig } from '../config/currentBotConfig';
import { BotConfigProvider, useBotConfig } from '../context/BotConfigContext';
import useAuthService from '../services/auth/authService';
import useLayoutService from '../services/layout/layoutService';
import applyTheme from '../theme/applyTheme';
import Chat from './chat/Chat';
import classes from './App.module.css';

function BotPageContent() {
  const authService = useAuthService();
  const authState = useSelector((state) => state.auth);
  const { texts } = useSelector((state) => state.translate);
  const config = useBotConfig();

  useLayoutService();

  useEffect(() => {
    authService.auth();
  }, []);

  useEffect(() => {
    if (authState.isAuth !== false || window.config?.autoLogin !== true) return;
    authService.accessRequest();
  }, [authState.isAuth]);

  if (authState.isAuth && !authState.isStudent) {
    return <Chat />;
  }

  return (
    <div className={classes.Cookies}>
      <div className={classes.CookiesText}>{!authState.isAuth ? config?.nonAuthText : texts.noPermission}</div>
      {!authState.isAuth && <button className={classes.CookiesButton} onClick={() => authService.accessRequest()}>
        {texts.grantPermission}
      </button>}
    </div>
  );
}

function ApplyThemeAndContent() {
  const config = useBotConfig();
  useEffect(() => {
    if (config?.theme) applyTheme(config.theme);
  }, [config?.theme]);
  return (
    <div className={classes.App}>
      <BotPageContent />
    </div>
  );
}

export default function BotPage() {
  const location = useLocation();
  const urlPathName = normalizeDirectUrlPath(location.pathname);
  const strippedPath = stripLegacyRegulatorySuffix(urlPathName);
  const [configWithUrl, setConfigWithUrl] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!urlPathName || strippedPath !== urlPathName) return undefined;
    let cancelled = false;
    setLoadFailed(false);
    setConfigWithUrl(null);

    ensureBotConfigForUrlPath(urlPathName)
      .then((config) => {
        if (cancelled) return;
        if (!config) {
          setLoadFailed(true);
          return;
        }
        const pathname = window.location.pathname.replace(/\/$/, '') || '/';
        const next = {
          ...config,
          botUrl: window.location.origin + (pathname === '/' ? '' : pathname)
        };
        setCurrentConfig(next);
        setConfigWithUrl(next);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [urlPathName, strippedPath]);

  if (!urlPathName || strippedPath !== urlPathName) {
    return <Navigate to={`${strippedPath ? `/${strippedPath}` : '/'}${location.search}`} replace />;
  }

  if (!configWithUrl && !loadFailed) {
    return null;
  }

  if (loadFailed || !configWithUrl) {
    return <Navigate to="/" replace />;
  }

  return (
    <BotConfigProvider value={configWithUrl}>
      <ApplyThemeAndContent />
    </BotConfigProvider>
  );
}